/**
 * Sync Service - Fetches sync data from Firestore via FirestoreService
 * 
 * Responsible for retrieving user data from Firebase backend for local sync.
 * Handles paginated queries and converts Firestore documents to DTOs.
 * 
 * Uses FirestoreService as an abstraction layer to facilitate future migration
 * to CloudFunctionsService.
 * 
 * Matches Flutter's SyncDataSource from lib/data/data_sources/sync_data_source.dart
 */

import type { PageCursor } from '@/data/types/json';
import { auth } from '@/data/services/firebase';
import { FirestoreService } from '@/data/services/firestoreService';
import { SERVER_FETCH_CONSTANTS } from '@/domain/constants/serverFetchConstants';
import type {
  SyncContentLikesResponse,
  SyncCommentLikesResponse,
  SyncCommunityMessageLikesResponse,
  SyncSavedContentResponse,
  SyncSavedUsersResponse,
  SyncUnlockedContentResponse,
} from '@/data/dtos/sync/syncResponses';
import type { ExclusiveContentSubscriptionDto } from '@/data/dtos/exclusiveContentSubscription/exclusiveContentSubscriptionDto';
import type { UserSettingDto } from '@/data/dtos/userSetting/userSettingDto';
import type { UserPremiumPackageDto } from '@/data/dtos/userPremiumPackage/userPremiumPackageDto';
import type { PremiumPackageDto } from '@/data/dtos/premiumPackage/premiumPackageDto';
import type { GiftDto } from '@/data/dtos/gift/giftDto';
import type { RechargeWalletProductDto } from '@/data/dtos/rechargeWalletProduct/rechargeWalletProductDto';
import type { RoadmapItemLikeDto } from '@/data/dtos/roadmapItemLike/roadmapItemLikeDto';
import type { RoadmapCommentLikeDto } from '@/data/dtos/roadmapCommentLike/roadmapCommentLikeDto';
import type { DonationProductDto } from '@/data/dtos/donationProduct/donationProductDto';
import type { WalletDto } from '@/data/dtos/wallet/walletDto';
import { UserMediaLikeRemapper } from '@/domain/remappers/userMediaLike/userMediaLikeRemapper';
import { CommentLikeRemapper } from '@/domain/remappers/commentLike/commentLikeRemapper';
import { CommunityMessageLikeRemapper } from '@/domain/remappers/communityMessageLike/communityMessageLikeRemapper';
import { SavedUserRemapper } from '@/domain/remappers/savedUser/savedUserRemapper';
import { UnlockedContentRemapper } from '@/domain/remappers/unlockedContent/unlockedContentRemapper';
import { UserSettingRemapper } from '@/domain/remappers/userSetting/userSettingRemapper';
import { RechargeWalletProductRemapper } from '@/domain/remappers/rechargeWalletProduct/rechargeWalletProductRemapper';
import { DonationProductRemapper } from '@/domain/remappers/donationProduct/donationProductRemapper';
import { RoadmapItemLikeRemapper } from '@/domain/remappers/roadmapItemLike/roadmapItemLikeRemapper';
import { RoadmapCommentLikeRemapper } from '@/domain/remappers/roadmapCommentLike/roadmapCommentLikeRemapper';
import { WalletRemapper } from '@/domain/remappers/wallet/walletRemapper';
import { SavedContentRemapper } from '@/domain/remappers/savedContent/savedContentRemapper';
import { GiftRemapper } from '@/domain/remappers/gift/giftRemapper';
import { ExclusiveContentSubscriptionRemapper } from '@/domain/remappers/exclusiveContentSubscription/exclusiveContentSubscriptionRemapper';
import { UserPremiumPackageRemapper } from '@/domain/remappers/userPremiumPackage/userPremiumPackageRemapper';
import { PremiumPackageRemapper } from '@/domain/remappers/premiumPackage/premiumPackageRemapper';
import { appLogger } from '@/domain/services/analytics/appLogger';

/**
 * Helper to get current user ID
 */
function getCurrentUserId(): string | null {
  return auth.currentUser?.uid || null;
}

/**
 * Sync Service - Handles all sync data fetching from Firebase
 */
export class SyncService {
  private firestoreService: FirestoreService;

  constructor(firestoreService?: FirestoreService) {
    this.firestoreService = firestoreService || FirestoreService.getInstance();
  }

  /**
   * Sync user's liked content (videos/media)
   * Paginated query to handle large datasets
   */
  async syncContentLikes(
    cursor?: PageCursor,
    limitCount: number = SERVER_FETCH_CONSTANTS.SYNC_PAGINATION_LIMIT
  ): Promise<SyncContentLikesResponse> {
    const userId = getCurrentUserId();
    if (!userId) {
      throw new Error('User not authenticated');
    }

    try {
      const result = await this.firestoreService.getSyncContentLikes(
        userId,
        cursor,
        limitCount
      );

      return {
        items: UserMediaLikeRemapper.fromJsonList(result.items),
        hasMore: result.hasMore,
        cursor: result.cursor,
      };
    } catch (error) {
      appLogger.error('syncContentLikes error', 'SyncService.syncContentLikes', { error: error as Error });
      return { items: [], hasMore: false };
    }
  }

  /**
   * Sync comment likes
   */
  async syncCommentLikes(
    cursor?: PageCursor,
    limitCount: number = SERVER_FETCH_CONSTANTS.SYNC_PAGINATION_LIMIT
  ): Promise<SyncCommentLikesResponse> {
    const userId = getCurrentUserId();
    if (!userId) {
      throw new Error('User not authenticated');
    }

    try {
      const result = await this.firestoreService.getSyncCommentLikes(
        userId,
        cursor,
        limitCount
      );

      return {
        items: CommentLikeRemapper.fromJsonList(result.items),
        hasMore: result.hasMore,
        cursor: result.cursor,
      };
    } catch (error) {
      appLogger.error('syncCommentLikes error', 'SyncService.syncCommentLikes', { error: error as Error });
      return { items: [], hasMore: false };
    }
  }

  /**
   * Sync community message likes
   */
  async syncCommunityMessageLikes(
    cursor?: PageCursor,
    limitCount: number = SERVER_FETCH_CONSTANTS.SYNC_PAGINATION_LIMIT
  ): Promise<SyncCommunityMessageLikesResponse> {
    const userId = getCurrentUserId();
    if (!userId) {
      throw new Error('User not authenticated');
    }

    try {
      const result = await this.firestoreService.getSyncCommunityMessageLikes(
        userId,
        cursor,
        limitCount
      );

      return {
        items: CommunityMessageLikeRemapper.fromJsonList(result.items),
        hasMore: result.hasMore,
        cursor: result.cursor,
      };
    } catch (error) {
      appLogger.error('syncCommunityMessageLikes error', 'SyncService.syncCommunityMessageLikes', { error: error as Error });
      return { items: [], hasMore: false };
    }
  }

  /**
   * Sync saved content (bookmarked videos)
   */
  async syncSavedContent(
    cursor?: PageCursor,
    limitCount: number = SERVER_FETCH_CONSTANTS.SYNC_PAGINATION_LIMIT
  ): Promise<SyncSavedContentResponse> {
    const userId = getCurrentUserId();
    if (!userId) {
      throw new Error('User not authenticated');
    }

    try {
      const result = await this.firestoreService.getSyncSavedContent(
        userId,
        cursor,
        limitCount
      );

      return {
        items: SavedContentRemapper.fromJsonList(result.items),
        hasMore: result.hasMore,
        cursor: result.cursor,
      };
    } catch (error) {
      appLogger.error('syncSavedContent error', 'SyncService.syncSavedContent', { error: error as Error });
      return { items: [], hasMore: false };
    }
  }

  /**
   * Sync saved users (bookmarked profiles)
   */
  async syncSavedUsers(
    cursor?: PageCursor,
    limitCount: number = SERVER_FETCH_CONSTANTS.SYNC_PAGINATION_LIMIT
  ): Promise<SyncSavedUsersResponse> {
    const userId = getCurrentUserId();
    if (!userId) {
      throw new Error('User not authenticated');
    }

    try {
      const result = await this.firestoreService.getSyncSavedUsers(
        userId,
        cursor,
        limitCount
      );

      return {
        items: SavedUserRemapper.fromJsonList(result.items),
        hasMore: result.hasMore,
        cursor: result.cursor,
      };
    } catch (error) {
      appLogger.error('syncSavedUsers error', 'SyncService.syncSavedUsers', { error: error as Error });
      return { items: [], hasMore: false };
    }
  }

  /**
   * Sync unlocked content (purchased videos)
   */
  async syncUnlockedContent(
    cursor?: PageCursor,
    limitCount: number = SERVER_FETCH_CONSTANTS.SYNC_PAGINATION_LIMIT
  ): Promise<SyncUnlockedContentResponse> {
    const userId = getCurrentUserId();
    if (!userId) {
      throw new Error('User not authenticated');
    }

    try {
      const result = await this.firestoreService.getSyncUnlockedContent(
        userId,
        cursor,
        limitCount
      );

      return {
        items: UnlockedContentRemapper.fromJsonList(result.items),
        hasMore: result.hasMore,
        cursor: result.cursor,
      };
    } catch (error) {
      appLogger.error('syncUnlockedContent error', 'SyncService.syncUnlockedContent', { error: error as Error });
      return { items: [], hasMore: false };
    }
  }

  /**
   * Get exclusive content subscriptions (non-paginated)
   */
  async getExclusiveContentSubscriptions(limitCount: number = SERVER_FETCH_CONSTANTS.MAX_SYNC_PAGINATION_LIMIT)
    : Promise<ExclusiveContentSubscriptionDto[]> {
    const userId = getCurrentUserId();
    if (!userId) {
      throw new Error('User not authenticated');
    }

    try {
      const items = await this.firestoreService.getSyncExclusiveContentSubscriptions(userId, limitCount);
      return ExclusiveContentSubscriptionRemapper.fromJsonList(items);
    } catch (error) {
      appLogger.error('getExclusiveContentSubscriptions error', 'SyncService.getExclusiveContentSubscriptions', { error: error as Error });
      return [];
    }
  }

  /**
   * Get user settings
   */
  async getUserSettings(): Promise<UserSettingDto[]> {
    const userId = getCurrentUserId();
    if (!userId) {
      throw new Error('User not authenticated');
    }

    try {
      const settings = await this.firestoreService.getUserSettings(userId);
      return UserSettingRemapper.fromJsonList(settings ?? []);
    } catch (error) {
      appLogger.error('getUserSettings error', 'SyncService.getUserSettings', { error: error as Error });
      return [];
    }
  }

  /**
   * Get the app-wide default settings from the global `/settings` collection.
   *
   * Mirrors Flutter's `UserSettingsDataSource.getDefaultSettings()`
   * (lib/data/data_sources/user_settings_data_source.dart:34-51), including its
   * two `[]` exits: no signed-in user (:36-39) and any read failure (:48-50).
   * Unlike `getUserSettings` above it never throws — the caller
   * (`SyncRemoteConfigSettingsChangesUseCase`) treats `[]` as "nothing to sync".
   */
  async getDefaultSettings(): Promise<UserSettingDto[]> {
    const userId = getCurrentUserId();
    if (!userId) {
      return [];
    }

    try {
      const settings = await this.firestoreService.getDefaultSettings();
      return UserSettingRemapper.fromJsonList(settings);
    } catch (error) {
      appLogger.error('getDefaultSettings error', 'SyncService.getDefaultSettings', { error: error as Error });
      return [];
    }
  }

  /**
   * Rewrite the signed-in user's `userSettings` subcollection from the supplied
   * default settings (clear-then-write, handled by
   * `FirestoreService.replaceUserSettings`).
   *
   * Mirrors Flutter's `UserSettingsDataSource.syncUserSettings(settings)`
   * (lib/data/data_sources/user_settings_data_source.dart:125-154); the per-document
   * write body is `UserSetting.toSettingUpdateInfo()`
   * (lib/data/models/user_setting.dart:60-72), owned by `UserSettingRemapper`.
   *
   * Returns false when signed out — Flutter dereferences the current user with `!`
   * at :135 and would throw; the web bails instead, and the caller stops there.
   */
  async saveUserSettingsFromDefaults(defaults: UserSettingDto[]): Promise<boolean> {
    const userId = getCurrentUserId();
    if (!userId) {
      return false;
    }

    return await this.firestoreService.replaceUserSettings(
      userId,
      defaults.map((setting) => UserSettingRemapper.toSettingUpdateInfo(setting))
    );
  }

  /**
   * Get user's active premium packages
   */
  async getUserPremiumPackages(): Promise<UserPremiumPackageDto[]> {
    const userId = getCurrentUserId();
    if (!userId) {
      throw new Error('User not authenticated');
    }

    try {
      const items = await this.firestoreService.getSyncUserPremiumPackages(userId);
      return UserPremiumPackageRemapper.fromJsonList(items);
    } catch (error) {
      appLogger.error('getUserPremiumPackages error', 'SyncService.getUserPremiumPackages', { error: error as Error });
      return [];
    }
  }

  /**
   * Get available premium packages (app-level)
   */
  async getPremiumPackages(): Promise<PremiumPackageDto[]> {
    try {
      const items = await this.firestoreService.getSyncPremiumPackages();
      return PremiumPackageRemapper.fromJsonList(items);
    } catch (error) {
      appLogger.error('getPremiumPackages error', 'SyncService.getPremiumPackages', { error: error as Error });
      return [];
    }
  }

  /**
   * Get available gifts (app-level)
   */
  async getGifts(): Promise<GiftDto[]> {
    try {
      const items = await this.firestoreService.getSyncGifts();
      return GiftRemapper.fromJsonList(items);
    } catch (error) {
      appLogger.error('getGifts error', 'SyncService.getGifts', { error: error as Error });
      return [];
    }
  }

  /**
   * Get recharge wallet products (app-level)
   */
  async getRechargeWalletProducts(): Promise<RechargeWalletProductDto[]> {
    try {
      const items = await this.firestoreService.getSyncRechargeWalletProducts();
      return RechargeWalletProductRemapper.fromJsonList(items);
    } catch (error) {
      appLogger.error('getRechargeWalletProducts error', 'SyncService.getRechargeWalletProducts', { error: error as Error });
      return [];
    }
  }

  /**
   * Get donation products (app-level)
   */
  async getDonationProducts(): Promise<DonationProductDto[]> {
    try {
      const items = await this.firestoreService.getSyncDonationProducts();
      return DonationProductRemapper.fromJsonList(items);
    } catch (error) {
      appLogger.error('getDonationProducts error', 'SyncService.getDonationProducts', { error: error as Error });
      return [];
    }
  }

  /**
   * Get user's wallet data
   */
  async getWallet(): Promise<WalletDto | null> {
    const userId = getCurrentUserId();
    if (!userId) {
      throw new Error('User not authenticated');
    }

    try {
      const wallet = await this.firestoreService.getSyncWallet(userId);
      return wallet ? WalletRemapper.fromJson(wallet) : null;
    } catch (error) {
      appLogger.error('getWallet error', 'SyncService.getWallet', { error: error as Error });
      return null;
    }
  }

  /**
   * Sync roadmap item likes (paginated)
   */
  async syncRoadmapLikes(
    cursor?: PageCursor,
    limitCount: number = SERVER_FETCH_CONSTANTS.SYNC_PAGINATION_LIMIT
  ): Promise<{ items: RoadmapItemLikeDto[]; hasMore: boolean; cursor?: PageCursor }> {
    const userId = getCurrentUserId();
    if (!userId) {
      throw new Error('User not authenticated');
    }

    try {
      const result = await this.firestoreService.getSyncRoadmapLikes(
        userId,
        cursor,
        limitCount
      );

      return {
        items: RoadmapItemLikeRemapper.fromJsonList(result.items),
        hasMore: result.hasMore,
        cursor: result.cursor,
      };
    } catch (error) {
      appLogger.error('syncRoadmapLikes error', 'SyncService.syncRoadmapLikes', { error: error as Error });
      return { items: [], hasMore: false };
    }
  }

  /**
   * Sync roadmap comment likes (paginated)
   */
  async syncRoadmapCommentLikes(
    cursor?: PageCursor,
    limitCount: number = SERVER_FETCH_CONSTANTS.SYNC_PAGINATION_LIMIT
  ): Promise<{ items: RoadmapCommentLikeDto[]; hasMore: boolean; cursor?: PageCursor }> {
    const userId = getCurrentUserId();
    if (!userId) {
      throw new Error('User not authenticated');
    }

    try {
      const result = await this.firestoreService.getSyncRoadmapCommentLikes(
        userId,
        cursor,
        limitCount
      );

      return {
        items: RoadmapCommentLikeRemapper.fromJsonList(result.items),
        hasMore: result.hasMore,
        cursor: result.cursor,
      };
    } catch (error) {
      appLogger.error('syncRoadmapCommentLikes error', 'SyncService.syncRoadmapCommentLikes', { error: error as Error });
      return { items: [], hasMore: false };
    }
  }
}
